import React, { useRef, useState, useEffect, FC, SVGProps } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SwiperCore from 'swiper';
import Marquee from 'react-fast-marquee';

// Case Study Covers
import LenskartCover from '@/assets/success-cover/lenskart.png';
import BluestoneCover from '@/assets/success-cover/bluestone.png';
import GivaCover from '@/assets/success-cover/giva.png';
import NestrootsCover from '@/assets/success-cover/nestroots.png';
import IZIcartCover from "@/assets/success-cover/izicart.png";
import MecTuringCover from "@/assets/success-cover/mecturing.png";
import AdivaaCover from "@/assets/success-cover/adivaa.png";

// Brand Logos
import LenskartLogo from "@/assets/logos/Lenskart.png";
import BluestoneLogo from "@/assets/logos/bluestone.png";
import GivaLogo from "@/assets/logos/Giva.png";
import NestrootsLogo from "@/assets/logos/nestRoot.png";
import IZIcartLogo from "@/assets/logos/IZIcart.png";
import MecTuringLogo from "@/assets/logos/MecTuring.png";
import AdivaaLogo from "@/assets/logos/Adivaa.png";

import AppliancesCover from "@/assets/icons/appliance.svg?react";
import AutoCover from "@/assets/icons/car.svg?react";
import BeautyCover from "@/assets/icons/lipstick.svg?react";
import BikesCover from "@/assets/icons/cycle.svg?react";
import ElectronicsCover from "@/assets/icons/laptop.svg?react";
import FitnessCover from "@/assets/icons/dumbell.svg?react";
import FurnitureCover from "@/assets/icons/bed.svg?react";
import JewelryCover from "@/assets/icons/ring.svg?react";
import OutdoorCover from "@/assets/icons/mountain.svg?react";
import SmartHomeCover from "@/assets/icons/wifi.svg?react";
import VideoCover from "@/assets/icons/television.svg?react";

// const logos = [
//     WakefitLogo, LenskartLogo, BluestoneLogo, GivaLogo, HomeCentreLogo, UrbanLadderLogo,
// ];

const logos = [
  MecTuringLogo, IZIcartLogo, AdivaaLogo,
  LenskartLogo, BluestoneLogo, GivaLogo, NestrootsLogo,
];

const caseStudies = [
  {
    brand: 'MecTURING',
    logo: MecTuringLogo,
    percentage: '2x',
    metric: 'ATTACH RATE',
    description: <>MecTURING is built around effortless, automated living. With Protega, we extended that experience beyond purchase, resulting in a <span className="tw-text-[#00C5FB]">2x increase in attach rate</span> as customers felt more confident investing in smart home technology.</>,
    image: MecTuringCover,
  },
  {
    brand: 'IZI',
    logo: IZIcartLogo,
    percentage: '10%',
    metric: 'DECREASE IN CAC',
    description: <>After launching co-branded campaigns with Protega, IZI saw a <span className="tw-text-[#00C5FB]">10% reduction in customer acquisition costs.</span> By combining product innovation with post-purchase protection, the campaigns drove stronger customer confidence and delivered consistent performance <span className="tw-text-[#00C5FB]">over 8+ months.</span></>,
    image: IZIcartCover,
  },
  {
    brand: 'Adivaa',
    logo: AdivaaLogo,
    percentage: '₹4M',
    metric: 'ATTACH RATE',
    description: <>Adivaa achieved <span className="tw-text-[#00C5FB]">₹4M in protection plan sales</span> after partnering with Protega, with a <span className="tw-text-[#00C5FB]">sitewide conversion rate of 12%.</span> The combination of advanced hydrogen-based wellness products and dependable protection enhanced customer decision-making and purchase confidence.</>,
    image: AdivaaCover,
  },
  // {
  //     brand: 'Wakefit',
  //     logo: WakefitLogo,
  //     percentage: '10%',
  //     metric: 'DECREASE IN CAC',
  //     description: <>After launching co-branded social media ads featuring Protega, Wakefit reduced customer acquisition costs by 10%. The ads were <span className="tw-text-[#00C5FB]">successful over 8+ months.</span></>,
  //     image: WakefitCover,
  // },
  {
    brand: 'Lenskart',
    logo: LenskartLogo,
    percentage: '2x',
    metric: 'ATTACH RATE',
    description: <>Lenskart <span className="tw-text-[#00C5FB]"> doubled their attach rate </span> after choosing Protega over their previous product protection provider.</>,
    image: LenskartCover,
  },
  {
    brand: 'Bluestone',
    logo: BluestoneLogo,
    percentage: '40%',
    metric: 'INCREASED CONVERSIONS',
    description: <>Protega enabled BlueStone to <span className="tw-text-[#00C5FB]"> establish their warranties portfolio </span> and increase conversions.</>,
    image: BluestoneCover,
  },
  {
    brand: 'Giva',
    logo: GivaLogo,
    percentage: '32%',
    metric: 'iINCREASED CONVERSION RATE',
    description: <>Giva built customer confidence with Protega, leading to a <span className="tw-text-[#00C5FB]">10.6% increase in conversion rate </span> year-over-year.</>,
    image: GivaCover,
  },
  {
  brand: 'Nestroots',
  logo: NestrootsLogo,
  percentage: '₹20M',
  metric: 'PRODUCT PROTECTION SALES',
  description: <>Since partnering with Protega, Nestroots has made ₹20M in product protection plan sales, led by a <span className="tw-text-[#00C5FB]"> sitewide conversion rate of 11%. </span></>,
  image: NestrootsCover,
}
  // {
  //   brand: 'UrbanLadder',
  //   logo: UrbanLadderLogo,
  //   percentage: '₹2.4M',
  //   metric: 'SAVED IN SHIPPING',
  //   description: <>With Protega, Urban Ladder has seen a <span className="tw-text-[#00C5FB]"> 45% Shipping Protection attach rate </span>, offsetting the risk and costs related to shipping losses.</>,
  //   image: UrbanLadderCover,
  // },
];

type Industry = {
  name: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  title: string;
};

const industries: Industry[] = [
  { name: 'Appliances', icon: AppliancesCover, title: 'Household Appliances' },
  { name: 'Automobile', icon: AutoCover, title: 'Auto' },
  { name: 'Beauty', icon: BeautyCover, title: 'Beauty & Personal Care' },
  { name: 'Bikes', icon: BikesCover, title: 'Bikes, Scooters, Skateboards' },
  { name: 'Fitness', icon: FitnessCover, title: 'Exercise & Fitness' },
  { name: 'Furniture', icon: FurnitureCover, title: 'Furniture' },
  { name: 'Electronics', icon: ElectronicsCover, title: 'Electronics' },
  { name: 'Jewelry', icon: JewelryCover, title: 'Jewelry' },
  { name: 'Outdoor', icon: OutdoorCover, title: 'Outdoor & Recreation' },
  { name: 'Smart Home', icon: SmartHomeCover, title: 'Smart Home & Network' },
  { name: 'Video', icon: VideoCover, title: 'TV, Audio, Video' },
];

const SuccessStudy: React.FC = () => {
  const swiperRef = useRef<SwiperCore>();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    if (!carouselRef.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      carouselRef.current!.classList.add('tw-translate-y-0', 'tw-opacity-100');
      obs.disconnect();
    }, { threshold: 0.2 });
    obs.observe(carouselRef.current);
    return () => obs.disconnect();
  }, []);

  const handleLogoClick = (index: number) => {
    setActiveIndex(index);
    swiperRef.current?.slideTo(index);
  };

  return (
    <section className="tw-bg-black tw-py-24 tw-px-2 md:tw-px-12">
      <div className="tw-max-w-7xl tw-mx-auto">
        <div className="tw-text-center tw-mb-16">
          <h2 className="tw-text-[28px] sm:tw-text-[36px] md:tw-text-[42px] lg:tw-text-[48px] tw-font-extrabold tw-text-white tw-leading-[1.1] tw-mb-6 md:tw-mb-6">
            Real results from <br />
            <span className="tw-text-[#00C5FB]">Real Protega merchants.</span>
          </h2>
          <p className="tw-text-base sm:tw-text-lg md:tw-text-xl tw-text-gray-400 tw-leading-relaxed">
            Protega is trusted by hundreds of merchant partners.
          </p>
          <p className="tw-text-base sm:tw-text-lg md:tw-text-xl tw-text-gray-400 tw-leading-relaxed">
            We deliver positive customer experiences and increased margins.
          </p>
        </div>

        <div className="tw-hidden md:tw-flex tw-justify-center tw-mb-12">
          {logos.map((name, i) => (
            <div key={i}>
              <div onClick={() => handleLogoClick(i)} className="tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-mx-4">
                <img
                  src={logos[i]}
                  alt={name}
                  className={`tw-h-10 tw-object-contain tw-transition-all ${activeIndex === i ? 'tw-filter tw-brightness-150' : 'tw-filter tw-brightness-75'}`}
                />
              </div>
              <div className={`tw-mt-4 tw-h-[2px] tw-w-full tw-transition-all ${activeIndex === i ? 'tw-bg-[#00C5FB]' : 'tw-bg-gray-800'}`} />
            </div>
          ))}
        </div>

        <div className="tw-relative tw-w-full">
          <button className="custom-swiper-prev tw-hidden md:tw-flex tw-absolute tw-top-1/2 tw-left-0 -tw-translate-y-1/2 tw-z-10  tw-text-white  tw-w-10 tw-h-10 tw-items-center tw-justify-center hover:tw-text-[#00C5FB] tw-transition" aria-label="Previous Slide">
            <ChevronLeft size={40} />
          </button>
          <button className="custom-swiper-next tw-hidden md:tw-flex tw-absolute tw-top-1/2 tw-right-0 -tw-translate-y-1/2 tw-z-10 tw-text-white tw-w-10 tw-h-10 tw-items-center tw-justify-center hover:tw-text-[#00C5FB] tw-transition" aria-label="Next Slide">
            <ChevronRight size={40} />
          </button>

          <Swiper
            modules={[Pagination, Navigation]}
            slidesPerView={1}
            spaceBetween={30}
            loop
            navigation={{
              nextEl: '.custom-swiper-next',
              prevEl: '.custom-swiper-prev',
            }}
            pagination={{
              el: '.custom-pagination',
              clickable: true
            }}
            onSlideChange={(swiper: any) => setActiveIndex(swiper.realIndex)}
            onSwiper={(swiper: any) => { swiperRef.current = swiper; }}
            className="tw-mx-12"
          >
            {caseStudies.map((study, idx) => (
              <SwiperSlide key={idx}>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-8 tw-bg-[#111] tw-rounded-2xl tw-overflow-hidden tw-w-full tw-max-h-screen">
                  <div className="tw-p-6 md:tw-p-10 tw-flex tw-flex-col tw-max-w-md tw-mx-auto tw-h-full">
                    <div className="tw-space-y-1">
                      <img src={study.logo} alt={study.brand} className="tw-h-10 tw-filter tw-brightness-200" />
                    </div>
                    <div className="tw-flex-grow">
                      <div className="tw-text-5xl md:tw-text-6xl lg:tw-text-8xl tw-font-normal tw-text-[#00C5FB] tw-leading-none tw-mt-8">
                        {study.percentage}
                      </div>
                      <div className="tw-text-sm tw-tracking-widest tw-uppercase tw-font-semibold tw-text-[#00C5FB] tw-mt-2">
                        {study.metric}
                      </div>
                      <p className="tw-text-gray-300 tw-font-semibold tw-mt-6 tw-leading-relaxed">
                        {study.description}
                      </p>
                    </div>
                    <button className="tw-mt-10 md:tw-mt-16 tw-bg-[#00C5FB] tw-text-black tw-px-12 tw-py-3 tw-rounded-full tw-w-fit hover:tw-bg-white tw-transition-colors tw-duration-500">
                      Read More
                    </button>
                  </div>
                  <div className="tw-hidden sm:tw-flex tw-items-center tw-justify-center tw-p-0 tw-h-full">
                    <img src={study.image} alt={`${study.brand} Image`} className="tw-object-cover tw-w-full tw-h-full" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="custom-pagination tw-flex tw-space-x-2 tw-justify-center tw-mt-6 md:tw-hidden" />
        </div>

        <div ref={carouselRef} className="tw-relative tw-w-screen tw-left-1/2 tw-ml-[-50vw] tw-translate-y-8 tw-opacity-0 tw-transition-all tw-duration-700 tw-ease-out tw-delay-300 tw-mt-16">
          <Marquee speed={100} gradient={false} pauseOnHover>
            {industries.map((industry, i) => (
              <div key={i} className="tw-bg-[#0F0F0F] tw-w-[250px]  tw-h-[80px] tw-flex-shrink-0 tw-flex tw-items-center tw-justify-between tw-px-6 tw-rounded-[20px] tw-mr-6">
                <p className="tw-text-[#4F4F4F] tw-text-lg tw-font-medium tw-pr-2">{industry.title}</p>
                <industry.icon className="tw-h-[40px] tw-w-[40px] tw-opacity-60" />
              </div>
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
};

export default SuccessStudy;
